/**
 * Staff control for a weight_tracked product's POUND POOL on the store inventory
 * page. Two pound entries, both routed through the canonical adjustment path
 * (never a direct write): a physical COUNT that SETS the pool, and a signed
 * ADJUST delta for shrink / receiving corrections. Non-weight_tracked rows keep
 * their integer stepper (this component is only rendered for weight_tracked).
 */
import React, { useState } from 'react';
import { storeInventoryAPI } from '../../services/storeAPI';

interface Props {
  itemId: number;        // StoreInventory row id; 0 when the product has no pool row yet
  productId: number;
  quantityLbs: number;
  // Update the row's pounds IN PLACE from the response (no page reload) — matches
  // how the integer stepper reconciles. id is present only when a Set created the row.
  onUpdated: (patch: { quantity_lbs: number; id?: number }) => void;
  onError: (msg: string) => void;
}

const inp: React.CSSProperties = {
  width: 74, padding: '4px 6px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13,
};
const btn: React.CSSProperties = {
  padding: '4px 10px', border: 'none', borderRadius: 6, background: '#508590',
  color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 30,
};
const btnAlt: React.CSSProperties = { ...btn, background: '#6B7280' };

export const WeightPoolCell: React.FC<Props> = ({ itemId, productId, quantityLbs, onUpdated, onError }) => {
  const [countVal, setCountVal] = useState('');
  const [adjVal, setAdjVal] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<{ quantity_lbs: number; id?: number }>) => {
    setBusy(true);
    try { onUpdated(await fn()); }
    catch (e: any) { onError(e.message || 'Update failed'); }
    finally { setBusy(false); }
  };

  const doSet = () => {
    const v = parseFloat(countVal);
    if (isNaN(v) || v < 0) { onError('Enter the counted pounds.'); return; }
    run(async () => {
      // No pool row yet → create it seeded at the counted weight; else SET it.
      let patch: { quantity_lbs: number; id?: number };
      if (!itemId) {
        const res = await storeInventoryAPI.create({ product_id: productId, quantity_lbs: v } as any);
        patch = { quantity_lbs: res.quantity_lbs ?? v, id: res.id };
      } else {
        const res = await storeInventoryAPI.countLbs(itemId, v, `Physical count ${v} lbs`);
        patch = { quantity_lbs: res.new_quantity_lbs ?? v };
      }
      setCountVal('');
      return patch;
    });
  };

  const doAdjust = () => {
    const v = parseFloat(adjVal);
    if (isNaN(v) || v === 0) { onError('Enter a +/- pounds amount.'); return; }
    if (!itemId) { onError('Set a count first to start the pool.'); return; }
    run(async () => {
      const res = await storeInventoryAPI.adjustLbs(itemId, v, 'COUNT_ADJUST', `Adjust ${v > 0 ? '+' : ''}${v} lbs`);
      setAdjVal('');
      return { quantity_lbs: res.new_quantity_lbs };
    });
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="number" step="0.01" min="0" placeholder="count lbs" value={countVal}
        onChange={(e) => setCountVal(e.target.value)} style={inp} disabled={busy} />
      <button onClick={doSet} disabled={busy || !countVal} style={btn} title="Set pool to this physical count">
        Set
      </button>
      <input type="number" step="0.01" placeholder="± lbs" value={adjVal}
        onChange={(e) => setAdjVal(e.target.value)} style={inp} disabled={busy} />
      <button onClick={doAdjust} disabled={busy || !adjVal} style={btnAlt} title="Add/remove pounds (shrink, receiving)">
        Adjust
      </button>
    </div>
  );
};
